/**
 * Product Main Section JavaScript
 * Handles: Gallery navigation, Expandable description, Quantity selector,
 *          Add to cart, Accordion FAQ, Bundle upsell
 */

class ProductMain {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;

    // State
    this.currentImageIndex = 0;
    this.quantity = 1;
    this.isExpanded = false;
    this.selectedBundleItems = new Map();

    this.init();
  }

  init() {
    this.cacheDOM();
    this.initGallery();
    this.initExpandableDescription();
    this.initQuantitySelector();
    this.initVariantPicker();
    this.initAddToCart();
    this.initBundleOptions();
    this.initAddons();
    // Accordions handled by global accordion.js
    this.initBundleUpsell();
    this.initStickyATC();
  }

  cacheDOM() {
    // Gallery
    this.mainImage = this.section.querySelector('[data-main-image] img');
    this.thumbnails = this.section.querySelectorAll('[data-thumbnail]');
    this.thumbnailsTrack = this.section.querySelector('[data-thumbnails-track]');
    this.thumbnailsNext = this.section.querySelector('[data-thumbnails-next]');

    // Description
    this.descriptionWrapper = this.section.querySelector('[data-expandable]');
    this.readMoreBtn = this.section.querySelector('[data-toggle-expand]');

    // Quantity
    this.quantitySelect = this.section.querySelector('[data-quantity-select]');
    this.quantityDisplay = this.section.querySelector('[data-quantity-display]');

    // Add to Cart
    this.addToCartBtn = this.section.querySelector('[data-add-to-cart]');

    // Bundle
    this.bundleSection = this.section.querySelector('[data-bundle-upsell]');
    this.bundleToggles = this.section.querySelectorAll('[data-bundle-toggle]');
    this.bundleItems = this.section.querySelectorAll('[data-bundle-item]');
    this.addBundleBtn = this.section.querySelector('[data-add-bundle]');
    this.bundleTotalDisplay = this.section.querySelector('[data-bundle-total]');
    this.bundleMessageEl = this.section.querySelector('[data-bundle-message]');
  }

  // ========================================
  // Gallery (Swiper with Thumbs)
  // ========================================
  initGallery() {
    const gallery = this.section.querySelector('[data-product-gallery]');
    if (!gallery) return;

    const galleryType = gallery.dataset.galleryType;

    if (galleryType === 'swiper-thumbs') {
      this.initSwiperWithThumbs();
    }
  }

  initSwiperWithThumbs() {
    const mainSwiperEl = this.section.querySelector('[data-main-swiper]');
    const thumbsSwiperEl = this.section.querySelector('[data-thumbs-swiper]');
    const thumbsNavBtn = this.section.querySelector('[data-thumbs-next]');
    const paginationEl = this.section.querySelector('[data-swiper-pagination]');
    const prevBtn = this.section.querySelector('[data-swiper-prev]');
    const nextBtn = this.section.querySelector('[data-swiper-next]');

    if (!mainSwiperEl || !thumbsSwiperEl) return;

    // Check if Swiper is available
    if (typeof Swiper === 'undefined') {
      console.warn('Swiper not loaded');
      return;
    }

    // Initialize Thumbs Swiper first
    this.thumbsSwiper = new Swiper(thumbsSwiperEl, {
      slidesPerView: 'auto',
      spaceBetween: 6,
      watchSlidesProgress: true,
      slideToClickedSlide: true
    });

    // Initialize Main Swiper with thumbs, navigation and pagination
    this.mainSwiper = new Swiper(mainSwiperEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      thumbs: {
        swiper: this.thumbsSwiper
      },
      pagination: paginationEl ? {
        el: paginationEl,
        clickable: true
      } : false,
      navigation: (prevBtn && nextBtn) ? {
        prevEl: prevBtn,
        nextEl: nextBtn
      } : false
    });

    // Video autoplay on slide change
    this.mainSwiper.on('slideChange', () => {
      this._handleVideoPlayback();
    });
    this._handleVideoPlayback();

    // Thumbs navigation button
    if (thumbsNavBtn) {
      thumbsNavBtn.addEventListener('click', () => {
        if (this.thumbsSwiper) {
          this.thumbsSwiper.slideNext();
        }
      });
    }
  }

  _handleVideoPlayback() {
    if (!this.mainSwiper) return;
    this.mainSwiper.slides.forEach((slide, index) => {
      const video = slide.querySelector('[data-product-video]');
      if (!video) return;
      if (index === this.mainSwiper.activeIndex) {
        video.play().catch(() => {});
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
  }

  // ========================================
  // Variant Picker
  // ========================================
  initVariantPicker() {
    const variantsJson = this.section.querySelector('[data-product-variants]');
    if (!variantsJson) return;

    try {
      this.variants = JSON.parse(variantsJson.textContent);
    } catch (e) {
      return;
    }

    this.variantPickers = this.section.querySelectorAll('[data-variant-picker]');
    if (!this.variantPickers.length) return;

    // Build current options from active buttons
    this.selectedOptions = [];
    this.variantPickers.forEach((picker) => {
      const active = picker.querySelector('.product-main__variant-btn.is-active');
      if (active) {
        this.selectedOptions.push(active.dataset.variantValue);
      }
    });

    // Bind click events
    this.variantPickers.forEach((picker) => {
      picker.querySelectorAll('.product-main__variant-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          const position = parseInt(btn.dataset.optionPosition, 10);
          const value = btn.dataset.variantValue;

          // Update active state in this picker
          picker.querySelectorAll('.product-main__variant-btn').forEach((b) => b.classList.remove('is-active'));
          btn.classList.add('is-active');

          // Update selected options
          this.selectedOptions[position - 1] = value;

          // Find matching variant
          const variant = this.findVariant();
          if (variant) {
            this.onVariantChange(variant);
          }
        });
      });
    });
  }

  findVariant() {
    return this.variants.find((v) => {
      return v.options.every((opt, i) => opt === this.selectedOptions[i]);
    });
  }

  onVariantChange(variant) {
    // Update price
    const priceEl = this.section.querySelector('[data-product-price]');
    const originalPriceEl = this.section.querySelector('[data-product-original-price]');
    const discountEl = this.section.querySelector('[data-product-discount]');
    const priceWrapper = this.section.querySelector('[data-product-price-wrapper]');

    if (priceEl) {
      priceEl.textContent = this.formatPrice(variant.price);
    }
    if (priceWrapper) {
      priceWrapper.dataset.originalPrice = variant.price;
    }

    // Handle compare_at_price
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      if (originalPriceEl) {
        originalPriceEl.textContent = this.formatPrice(variant.compare_at_price);
        originalPriceEl.style.display = '';
      }
      if (discountEl) {
        const pct = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
        discountEl.textContent = `-${pct}%`;
        discountEl.style.display = '';
      }
    } else {
      if (originalPriceEl) originalPriceEl.style.display = 'none';
      if (discountEl) discountEl.style.display = 'none';
    }

    // Update ATC button
    if (this.addToCartBtn) {
      this.addToCartBtn.dataset.variantId = variant.id;
      this.addToCartBtn.dataset.price = variant.price;
      if (variant.available) {
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.textContent = window.theme?.strings?.addToCart || 'Ajouter au panier';
      } else {
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.textContent = window.theme?.strings?.outOfStock || 'Rupture de stock';
      }
    }

    // Update sticky ATC
    if (this.stickyATCBtn) {
      this.stickyATCBtn.dataset.variantId = variant.id;
      if (variant.available) {
        this.stickyATCBtn.disabled = false;
        this.stickyATCBtn.textContent = window.theme?.strings?.addToCart || 'Ajouter au panier';
      } else {
        this.stickyATCBtn.disabled = true;
        this.stickyATCBtn.textContent = window.theme?.strings?.outOfStock || 'Rupture de stock';
      }
    }

    // Update sticky price
    const stickyPrice = this.section.querySelector('.product-main__sticky-atc-price');
    if (stickyPrice) {
      stickyPrice.textContent = this.formatPrice(variant.price);
    }

    // Complements a cocher : nouveaux libelles de base, puis recalcul du total
    if (this.addonsBlock) {
      this.atcBaseLabel = this.addToCartBtn ? this.addToCartBtn.textContent.trim() : this.atcBaseLabel;
      this.stickyBaseLabel = this.stickyATCBtn ? this.stickyATCBtn.textContent.trim() : this.stickyBaseLabel;
    }
    this.updateAddonsTotal();

    // Update URL without reload
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  // ========================================
  // Expandable Description
  // ========================================
  initExpandableDescription() {
    if (!this.readMoreBtn || !this.descriptionWrapper) return;

    this.readMoreBtn.addEventListener('click', () => {
      this.toggleReadMore();
    });
  }

  toggleReadMore() {
    this.isExpanded = !this.isExpanded;
    this.descriptionWrapper.classList.toggle('is-expanded', this.isExpanded);

    const iconEl = this.readMoreBtn.querySelector('[data-icon]');
    const textEl = this.readMoreBtn.querySelector('[data-text]');

    if (iconEl) {
      iconEl.textContent = this.isExpanded ? '-' : '+';
    }
    if (textEl) {
      const strings = window.theme?.strings || {};
      textEl.textContent = this.isExpanded ? (strings.readLess || 'Lire moins') : (strings.readMore || 'Lire plus');
    }
  }

  // ========================================
  // Quantity Selector
  // ========================================
  initQuantitySelector() {
    if (!this.quantitySelect || !this.quantityDisplay) return;

    this.quantitySelect.addEventListener('change', () => {
      this.quantity = parseInt(this.quantitySelect.value, 10);
      this.quantityDisplay.textContent = this.quantity;
      this.updateAddonsTotal();
      this.updateBundleTotal();
      this.updateBundleMessage();
    });
  }

  // ========================================
  // Format selector (1 / 2 flacons)
  // ========================================
  getSelectedQuantity() {
    const bundleRadio = this.section.querySelector('input[name="pm-bundle"]:checked');
    if (bundleRadio) return parseInt(bundleRadio.dataset.qty, 10) || 1;
    return this.quantitySelect ? parseInt(this.quantitySelect.value, 10) : 1;
  }

  initBundleOptions() {
    const radios = this.section.querySelectorAll('input[name="pm-bundle"]');
    if (!radios.length) return;
    const opts = this.section.querySelectorAll('.product-main__bopt');
    const sync = () =>
      opts.forEach((o) => o.classList.toggle('is-selected', o.querySelector('input').checked));
    radios.forEach((r) => r.addEventListener('change', sync));
    sync();
  }

  // ========================================
  // Complements a cocher (cremes) : cases "pm-addon" ajoutees dans le meme
  // /cart/add.js que le produit principal. Total = prix produit x quantite
  // + cremes cochees, formate d'apres l'echantillon Liquid data-money-sample.
  // ========================================
  getSelectedAddonIds() {
    return [...this.section.querySelectorAll('input[name="pm-addon"]:checked')]
      .map((c) => parseInt(c.value, 10))
      .filter((v) => v > 0);
  }

  initAddons() {
    this.addonsBlock = this.section.querySelector('[data-addons]');
    if (!this.addonsBlock || !this.addToCartBtn) return;
    this.addonInputs = this.addonsBlock.querySelectorAll('input[name="pm-addon"]');
    this.addonsLive = this.addonsBlock.querySelector('[data-addons-live]');
    this.atcBaseLabel = this.addToCartBtn.textContent.trim();
    this.stickyATCBtnRef = this.section.querySelector('[data-sticky-add-to-cart]');
    this.stickyBaseLabel = this.stickyATCBtnRef ? this.stickyATCBtnRef.textContent.trim() : '';
    this.moneyPattern = this.parseMoneySample(this.addonsBlock.dataset.moneySample);
    this.addonInputs.forEach((input) => {
      input.addEventListener('change', () => this.updateAddonsTotal(true));
    });
    // Si le selecteur 1 / 2 flacons est actif en meme temps, sa quantite compte aussi
    this.section.querySelectorAll('input[name="pm-bundle"]').forEach((r) => {
      r.addEventListener('change', () => this.updateAddonsTotal());
    });
    this.updateAddonsTotal();
  }

  // Deduit prefixe, suffixe, separateur de milliers et decimal de "123456 | money"
  // (ex : "CHF 1'234.56", "1 234,56 €", "€1.234,56").
  parseMoneySample(sample) {
    if (!sample) return null;
    const m = sample.match(/1(\D?)234(\D)56/);
    if (!m) return null;
    return {
      prefix: sample.slice(0, m.index),
      suffix: sample.slice(m.index + m[0].length),
      thousands: m[1] || '',
      decimal: m[2]
    };
  }

  formatMoney(cents) {
    const pat = this.moneyPattern;
    if (!pat) return this.formatPrice(cents);
    const whole = Math.floor(cents / 100);
    const frac = String(cents % 100).padStart(2, '0');
    const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, pat.thousands);
    return pat.prefix + grouped + pat.decimal + frac + pat.suffix;
  }

  // Pose « Libelle · total » sur un bouton (ou le libelle seul), sans toucher
  // a un bouton indisponible (rupture) ni a un bouton en cours d'ajout.
  setAtcLabel(btn, baseLabel, totalFormatted) {
    if (!btn || btn.disabled || btn.dataset.busy === '1') return;
    btn.textContent = baseLabel;
    btn.classList.toggle('has-total', !!totalFormatted);
    if (!totalFormatted) return;
    // Espace insecable : le bouton est en flex, un espace simple serait avale
    const sep = document.createElement('span');
    sep.className = 'product-main__atc-total';
    sep.textContent = '\u00a0\u00b7 ' + totalFormatted;
    btn.appendChild(sep);
  }

  updateAddonsTotal(announce) {
    if (!this.addonsBlock || !this.addToCartBtn) return;
    let addons = 0;
    let count = 0;
    this.addonInputs.forEach((input) => {
      const row = input.closest('.product-main__addon');
      if (row) row.classList.toggle('is-selected', input.checked);
      if (input.checked) {
        addons += parseInt(input.dataset.price, 10) || 0;
        count += 1;
      }
    });
    const unit = parseInt(this.addToCartBtn.dataset.price, 10) || 0;
    const qty = this.getSelectedQuantity();
    const total = unit * qty + addons;
    const formatted = count > 0 ? this.formatMoney(total) : '';
    const stickyPrice = this.section.querySelector('.product-main__sticky-atc-price');
    this.setAtcLabel(this.addToCartBtn, this.atcBaseLabel, formatted);
    this.setAtcLabel(this.stickyATCBtnRef, this.stickyBaseLabel, formatted);
    if (stickyPrice) {
      // Sans complement : prix de la variante courante (meme format que Liquid)
      stickyPrice.textContent = count > 0 ? formatted : this.formatMoney(unit * qty);
    }
    if (announce && this.addonsLive) {
      // Annonce du nouveau total aux lecteurs d'ecran (region live du snippet)
      const text = count > 0 ? formatted : this.formatMoney(unit * qty);
      this.addonsLive.textContent = '';
      window.setTimeout(() => { this.addonsLive.textContent = text; }, 50);
    }
  }

  // ========================================
  // Add to Cart
  // ========================================
  initAddToCart() {
    if (!this.addToCartBtn) return;

    this.addToCartBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const variantId = this.addToCartBtn.dataset.variantId;
      if (!variantId) return;

      // Read quantity from the format selector (1/2 flacons), else the quantity select
      const quantity = this.getSelectedQuantity();

      await this.addToCart(variantId, quantity, this.addToCartBtn, this.getSelectedAddonIds());
    });
  }

  async addToCart(variantId, quantity, btn, addonIds) {
    btn = btn || this.addToCartBtn;
    const originalHtml = btn.innerHTML;
    if (btn.dataset.busy === '1') return;
    btn.dataset.busy = '1';

    // Complements coches : une seule requete, plusieurs lignes. Les cremes d'abord,
    // le produit principal en dernier : le panier liste le dernier ajout en premier.
    const addons = Array.isArray(addonIds) ? addonIds : [];
    const payload = addons.length
      ? { items: addons.map((id) => ({ id: parseInt(id, 10), quantity: 1 }))
          .concat([{ id: parseInt(variantId, 10), quantity: quantity }]) }
      : { id: parseInt(variantId, 10), quantity: quantity };

    try {
      btn.disabled = true;
      btn.textContent = window.theme?.strings?.adding || 'Ajout en cours...';

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        // Shopify renvoie 422 avec une description localisee (ex : stock insuffisant
        // sur une des lignes) : on l'affiche au lieu d'un « Erreur » generique.
        // Seul ce texte Shopify est montre au client (jamais une erreur reseau brute).
        const err = new Error('Failed to add to cart');
        err.userMessage = data && (data.description || data.message) ? String(data.description || data.message) : '';
        throw err;
      }

      this.showAtcStatus(btn, '');

      // Check for checkout redirect
      if (this.section.dataset.redirectCheckout === 'true') {
        window.location.href = '/checkout';
        return;
      }

      // Refresh and open cart drawer
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:open'));

      // Success feedback
      btn.textContent = window.theme?.strings?.added || 'Ajouté !';

      setTimeout(() => this.restoreAtcButton(btn, originalHtml), 2000);

    } catch (error) {
      console.error('Add to cart error:', error);
      btn.textContent = window.theme?.strings?.error || 'Erreur';
      this.showAtcStatus(btn, (error && error.userMessage) || '');

      setTimeout(() => this.restoreAtcButton(btn, originalHtml), 2000);
    }
  }

  // Fin du retour visuel : on recalcule le libelle (les cases ont pu changer
  // pendant les 2 s) plutot que de restaurer un HTML fige.
  restoreAtcButton(btn, originalHtml) {
    btn.innerHTML = originalHtml;
    btn.disabled = false;
    delete btn.dataset.busy;
    this.updateAddonsTotal();
  }

  // Message d'etat sous le bouton (aria-live), vide pour effacer.
  // Rangee principale (flex en ligne, sans retour) : insere APRES la rangee.
  // Barre sticky (conteneur en colonne) : ajoute dans le conteneur.
  showAtcStatus(btn, message) {
    const row = btn.closest('.product-main__atc-row, .product-main__sticky-atc-action') || btn.parentElement;
    if (!row) return;
    const inRow = row.classList.contains('product-main__atc-row');
    let el = inRow
      ? (row.nextElementSibling && row.nextElementSibling.hasAttribute('data-atc-status') ? row.nextElementSibling : null)
      : row.querySelector('[data-atc-status]');
    if (!el) {
      el = document.createElement('p');
      el.className = 'product-main__atc-status p-extra-small';
      el.setAttribute('data-atc-status', '');
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.hidden = true;
      if (inRow) row.insertAdjacentElement('afterend', el); else row.appendChild(el);
    }
    if (!message) {
      el.textContent = '';
      el.hidden = true;
      return;
    }
    // Region rendue visible d'abord, texte ensuite : sinon l'annonce peut etre perdue
    el.textContent = '';
    el.hidden = false;
    window.setTimeout(() => { el.textContent = message; }, 50);
  }

  // ========================================
  // Bundle Upsell with Dynamic Discount Tiers
  // ========================================
  initBundleUpsell() {
    if (!this.bundleSection) return;

    this.discountMin = parseInt(this.section.dataset.discountMin, 10) || 2;
    this.discountPercent = parseInt(this.section.dataset.discountPercent, 10) || 10;
    this.cartCount = 0;
    this.currentDiscount = 0;

    // Fetch cart to compute bundle discount
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        this.cartCount = cart.item_count;
        this.applyBundleDiscount();
      })
      .catch(() => {});

    this.cartUpdateHandler = () => {
      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          this.cartCount = cart.item_count;
          this.applyBundleDiscount();
        })
        .catch(() => {});
    };

    // cart:updated fires when adding main product
    // cart:drawer-refreshed fires after any cart:refresh (e.g. addBundleToCart)
    document.addEventListener('cart:updated', this.cartUpdateHandler);
    document.addEventListener('cart:drawer-refreshed', this.cartUpdateHandler);

    // Initialize selected items from default checked state
    this.bundleItems.forEach((item) => {
      const toggle = item.querySelector('[data-bundle-toggle]');
      const variantId = item.dataset.variantId;
      const price = parseInt(item.dataset.price, 10) || 0;

      if (toggle && toggle.checked) {
        this.selectedBundleItems.set(variantId, price);
      }
    });

    // Toggle event listeners
    this.bundleToggles.forEach((toggle) => {
      toggle.addEventListener('change', () => {
        this.updateBundleSelection();
      });
    });

    // Add bundle button
    if (this.addBundleBtn) {
      this.addBundleBtn.addEventListener('click', () => {
        this.addBundleToCart();
      });
    }

    // Initial update
    this.updateBundleSelection();
  }

  applyBundleDiscount() {
    const selectedBundleCount = this.selectedBundleItems ? this.selectedBundleItems.size : 0;
    const bundleTotal = this.cartCount + selectedBundleCount;
    this.currentDiscount = bundleTotal >= this.discountMin ? this.discountPercent : 0;
    this.updateBundleProductPrices();
    this.updateBundleTotal();
    this.updateBundleMessage();
  }

  updateBundleMessage() {
    if (!this.bundleMessageEl) return;

    const el = this.bundleMessageEl;
    const strAddPrefix = el.dataset.strAddPrefix || '';
    const strAddSuffix = el.dataset.strAddSuffix || '';
    const strInCartSuffix = el.dataset.strInCartSuffix || '';
    const discountMin = this.discountMin || 2;
    const discountPercent = this.discountPercent || 0;
    const cartCount = this.cartCount || 0;

    let textHtml;
    if (cartCount >= discountMin) {
      // Cart has enough items: "X produit(s) dans votre panier - Vous bénéficiez de"
      textHtml = `<span class="p-medium"><strong>${cartCount}</strong> ${strInCartSuffix}</span>`;
    } else {
      // Not enough yet: "Ajoutez X produit(s) au panier et bénéficiez de"
      textHtml = `<span class="p-medium">${strAddPrefix} <strong>${discountMin}</strong> ${strAddSuffix}</span>`;
    }

    el.innerHTML = textHtml + `<span class="product-main__bundle-discount p-medium" data-bundle-discount-badge>-${discountPercent}%</span>`;
  }

  updateBundleSelection() {
    this.selectedBundleItems.clear();

    this.bundleItems.forEach((item) => {
      const toggle = item.querySelector('[data-bundle-toggle]');
      const variantId = item.dataset.variantId;
      const price = parseInt(item.dataset.price, 10) || 0;

      if (toggle && toggle.checked) {
        this.selectedBundleItems.set(variantId, price);
        item.classList.add('is-selected');
      } else {
        item.classList.remove('is-selected');
      }
    });

    this.applyBundleDiscount();
  }

  updateBundleProductPrices() {
    if (!this.bundleSection) return;

    const pricingEls = this.bundleSection.querySelectorAll('[data-bundle-product-pricing]');
    const discount = this.currentDiscount || 0;

    pricingEls.forEach(el => {
      const originalPrice = parseInt(el.dataset.originalPrice, 10);
      const priceEl = el.querySelector('[data-bundle-product-price]');
      const originalEl = el.querySelector('[data-bundle-product-original]');
      const discountEl = el.querySelector('[data-bundle-product-discount]');

      if (!priceEl || !originalEl || !discountEl) return;

      if (discount > 0) {
        const discountedPrice = originalPrice * (1 - discount / 100);
        const discountAmount = originalPrice - discountedPrice;

        // Show original price with strikethrough
        originalEl.style.display = '';
        // Update current price to discounted
        priceEl.textContent = this.formatPrice(discountedPrice);
        // Show discount badge
        discountEl.textContent = `-${this.formatPrice(discountAmount)}`;
        discountEl.style.display = '';
      } else {
        // Hide original and discount badge
        originalEl.style.display = 'none';
        discountEl.style.display = 'none';
        // Show normal price
        priceEl.textContent = this.formatPrice(originalPrice);
      }
    });
  }

  formatPrice(cents) {
    const currency = this.section.dataset.currency || 'EUR';
    const locale = this.section.dataset.locale || document.documentElement.lang || 'fr';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(cents / 100);
  }

  updateBundleTotal() {
    if (!this.bundleTotalDisplay) return;

    // Calculate bundle total (ONLY bundle items, not main product)
    let subtotal = 0;
    this.selectedBundleItems.forEach((price) => {
      subtotal += price;
    });

    // Apply discount if applicable
    const discount = this.currentDiscount || 0;
    const total = subtotal * (1 - discount / 100);

    this.bundleTotalDisplay.textContent = this.formatPrice(total);
  }

  async addBundleToCart() {
    const btn = this.addBundleBtn;
    if (!btn) return;

    const originalText = btn.textContent;
    const items = [];

    // Add ONLY selected bundle items (not main product)
    this.selectedBundleItems.forEach((price, variantId) => {
      items.push({
        id: parseInt(variantId, 10),
        quantity: 1
      });
    });

    if (items.length === 0) return;

    try {
      btn.disabled = true;
      btn.textContent = window.theme?.strings?.adding || 'Ajout en cours...';

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        throw new Error('Failed to add bundle to cart');
      }

      const data = await response.json();

      // Refresh and open cart drawer
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:open'));

      // Success feedback
      btn.textContent = window.theme?.strings?.added || 'Ajouté !';

      setTimeout(() => {
        btn.innerHTML = originalText;
        btn.disabled = false;
      }, 2000);

    } catch (error) {
      console.error('Add bundle to cart error:', error);
      btn.textContent = window.theme?.strings?.error || 'Erreur';

      setTimeout(() => {
        btn.innerHTML = originalText;
        btn.disabled = false;
      }, 2000);
    }
  }

  // ========================================
  // Sticky Add to Cart
  // ========================================
  initStickyATC() {
    this.stickyBar = this.section.querySelector('[data-sticky-atc]');
    if (!this.stickyBar || !this.addToCartBtn) return;

    this.stickyATCBtn = this.stickyBar.querySelector('[data-sticky-add-to-cart]');
    this.stickyVisible = false;
    this.atcButtonVisible = true;

    // Observe main ATC button visibility
    this.atcObserver = new IntersectionObserver((entries) => {
      this.atcButtonVisible = entries[0].isIntersecting;
      this.updateStickyVisibility();
    }, { threshold: 0 });

    this.atcObserver.observe(this.addToCartBtn);

    // Scroll threshold check
    this.scrollHandler = () => this.updateStickyVisibility();
    window.addEventListener('scroll', this.scrollHandler, { passive: true });

    // Sticky button click
    if (this.stickyATCBtn) {
      this.stickyATCBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const variantId = this.stickyATCBtn.dataset.variantId;
        if (!variantId) return;
        const quantity = this.getSelectedQuantity();
        await this.addToCart(variantId, quantity, this.stickyATCBtn, this.getSelectedAddonIds());
      });
    }
  }

  updateStickyVisibility() {
    const atBottom = (window.innerHeight + window.scrollY) >= (document.body.scrollHeight - 50);
    const isDesktop = window.innerWidth >= 750;
    const pastThreshold = isDesktop ? window.scrollY > 400 : true;
    const shouldShow = !this.atcButtonVisible && pastThreshold && !atBottom;
    if (shouldShow !== this.stickyVisible) {
      this.stickyVisible = shouldShow;
      this.stickyBar.classList.toggle('is-visible', shouldShow);
      this.stickyBar.setAttribute('aria-hidden', String(!shouldShow));
    }
  }

  // ========================================
  // Cleanup for Theme Editor
  // ========================================
  destroy() {
    if (this.cartUpdateHandler) {
      document.removeEventListener('cart:updated', this.cartUpdateHandler);
      document.removeEventListener('cart:drawer-refreshed', this.cartUpdateHandler);
    }
    if (this.atcObserver) {
      this.atcObserver.disconnect();
    }
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
  }
}

// ========================================
// Initialization
// ========================================
function initProductMain() {
  document.querySelectorAll('[data-section-type="product-main"]').forEach((section) => {
    if (!section._productMainInstance) {
      section._productMainInstance = new ProductMain(section);
    }
  });
}

// Init on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductMain);
} else {
  initProductMain();
}

// Theme Editor support
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector('[data-section-type="product-main"]');
  if (section) {
    section._productMainInstance = new ProductMain(section);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target.querySelector('[data-section-type="product-main"]');
  if (section && section._productMainInstance) {
    section._productMainInstance.destroy();
    delete section._productMainInstance;
  }
});

// Block select/deselect handled by global accordion.js

/* ========================================
   PDP V2 - Urgency shipping countdown
   Counts down to the cutoff hour (data-cutoff-hour, local time).
   Past the cutoff, it targets the next day's cutoff.
   ======================================== */
(function () {
  function initUrgencyCountdowns(root) {
    var nodes = (root || document).querySelectorAll('[data-urgency-countdown]');
    if (!nodes.length) return;

    nodes.forEach(function (node) {
      if (node._urgencyTimer) clearInterval(node._urgencyTimer);
      var cutoffHour = parseInt(node.dataset.cutoffHour, 10);
      if (isNaN(cutoffHour)) cutoffHour = 18;

      function tick() {
        var now = new Date();
        var cutoff = new Date();
        cutoff.setHours(cutoffHour, 0, 0, 0);
        if (now >= cutoff) cutoff.setDate(cutoff.getDate() + 1);
        var s = Math.floor((cutoff - now) / 1000);
        var h = String(Math.floor(s / 3600)).padStart(2, '0');
        var m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
        var sec = String(s % 60).padStart(2, '0');
        node.textContent = h + ':' + m + ':' + sec;
      }

      tick();
      node._urgencyTimer = setInterval(tick, 1000);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initUrgencyCountdowns(document);
    });
  } else {
    initUrgencyCountdowns(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initUrgencyCountdowns(event.target);
  });
})();
